// packages/ui/src/auth/WrongPortalPage.tsx
//
// full-screen page rendered when an authenticated user lands
// on a portal they don't have scope for (e.g., merchant user opens
// admin-v3 in another tab). Offers:
//   - identity reminder (email + primary role)
//   - deep link to the correct portal (auto-derived from user payload)
//   - logout button (clear session, return to /login on this portal)

import { useAuth, type AuthUser } from '../hooks/useAuth.js';
import type { Portal } from './RoleGuard.js';

interface WrongPortalPageProps {
  user: AuthUser;
  currentPortal: Portal;
}

/**
 * Pick the portal that the user actually has access to. Priority:
 *   1. primaryRole === 'admin' OR roles[].admin → admin
 *   2. merchantId present → merchant
 *   3. vendorId present → vendor
 *   4. otherwise null (user has no portal — show logout-only page).
 */
const ADMIN_ROLE_TYPES = new Set(['admin', 'superadmin']);

function expectedPortal(user: AuthUser): Portal | null {
  if (
    ADMIN_ROLE_TYPES.has(user.primaryRole) ||
    (user.roles?.some((r) => ADMIN_ROLE_TYPES.has(r)) ?? false)
  ) {
    return 'admin';
  }
  if (user.merchantId) return 'merchant';
  if (user.vendorId) return 'vendor';
  return null;
}

/**
 * Build the canonical V3 portal URL from `window.location.hostname`.
 * Replaces the leading subdomain with `<portal>-v3`. For localhost /
 * IP literals this falls back to current origin (dev mode — all 3
 * SPAs run on different ports anyway).
 */
function buildPortalUrl(portal: Portal): string {
  if (typeof window === 'undefined') return '/';
  const host = window.location.hostname;
  // host = "admin-v3.printway.work" → split → ["admin-v3", "printway", "work"]
  // dev / localhost / IP: just return current origin (operator runs vite
  // separately per portal in dev).
  const parts = host.split('.');
  if (parts.length < 3) {
    return window.location.origin;
  }
  parts[0] = `${portal}-v3`;
  return `${window.location.protocol}//${parts.join('.')}/`;
}

export function WrongPortalPage({
  user,
  currentPortal,
}: WrongPortalPageProps): JSX.Element {
  const logout = useAuth((s) => s.logout);
  const target = expectedPortal(user);
  const targetUrl = target ? buildPortalUrl(target) : null;

  return (
    <main className="flex min-h-screen items-center justify-center bg-pw-surface px-4">
      <div className="w-full max-w-md rounded-lg border border-pw-border bg-white p-8 shadow-sm">
        <h1 className="mb-2 text-xl font-semibold text-pw-text">
          Bạn đang ở sai cổng
        </h1>
        <p className="mb-6 text-sm text-pw-text-muted">
          Bạn đã đăng nhập là <strong>{user.email}</strong> ({user.primaryRole}
          ), nhưng cổng <code>{currentPortal}-v3</code> không cấp quyền cho
          tài khoản này.
        </p>

        <div className="flex flex-col gap-3">
          {targetUrl && target ? (
            <a
              href={targetUrl}
              className="inline-flex items-center justify-center rounded-md bg-pw-primary px-4 py-2 text-sm font-medium text-white hover:bg-pw-primary/90"
            >
              Chuyển sang {target}-v3
            </a>
          ) : (
            <p className="text-sm text-pw-text-muted">
              Tài khoản của bạn hiện không có cổng phù hợp. Liên hệ admin để
              được cấp quyền.
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              logout().catch(() => {
                /* swallow — best-effort */
              });
            }}
            className="inline-flex items-center justify-center rounded-md border border-pw-border bg-white px-4 py-2 text-sm font-medium text-pw-text hover:bg-pw-surface"
          >
            Đăng xuất
          </button>
        </div>
      </div>
    </main>
  );
}
